import React, { useEffect, useState } from 'react';
import {
    createEstimateDuelRound,
    GameResultOverlay,
    GameShell,
} from '../shared';
import brainGameLevels from '../../../lib/brainGameLevels';
import { formatGameCopy } from './gameCopy';

const { normalizeBrainGameLevel } = brainGameLevels;
const ESTIMATE_LEVEL_SPAN = 6;
const ESTIMATE_MAX_DIFFICULTY_ROUND = 18;
const LEVEL_UP_STREAK = 3;

const getEstimateDifficultyRound = (level) => Math.min(
    ESTIMATE_MAX_DIFFICULTY_ROUND,
    ((level - 1) * ESTIMATE_LEVEL_SPAN) + 1
);

function EstimateDuelGame({ copy = {}, initialLevel, ...props }) {
    const startingLevel = normalizeBrainGameLevel('estimate-duel', initialLevel);
    const [trialIndex, setTrialIndex] = useState(1);
    const [level, setLevel] = useState(startingLevel);
    const [trial, setTrial] = useState(() => createEstimateDuelRound(getEstimateDifficultyRound(startingLevel)));
    const [choice, setChoice] = useState('');
    const [score, setScore] = useState(0);
    const [streak, setStreak] = useState(0);
    const maxRounds = 18;
    const done = trialIndex > maxRounds;

    const reset = () => {
        setTrialIndex(1);
        setLevel(startingLevel);
        setTrial(createEstimateDuelRound(getEstimateDifficultyRound(startingLevel)));
        setChoice('');
        setScore(0);
        setStreak(0);
    };

    const choose = (side) => {
        if (done || choice) {
            return;
        }

        const correct = side === trial.correctSide;
        const nextStreak = correct ? streak + 1 : 0;
        setScore((current) => current + (correct ? 1 : 0));
        setStreak(nextStreak);
        setLevel((current) => normalizeBrainGameLevel(
            'estimate-duel',
            current + (correct && nextStreak % LEVEL_UP_STREAK === 0 ? 1 : 0)
        ));
        setChoice(side);
    };

    const nextRound = () => {
        const nextTrialIndex = trialIndex + 1;
        setTrialIndex(nextTrialIndex);
        setChoice('');
        if (nextTrialIndex <= maxRounds) {
            setTrial(createEstimateDuelRound(getEstimateDifficultyRound(level)));
        }
    };

    useEffect(() => {
        if (!choice || done) {
            return undefined;
        }

        const timer = window.setTimeout(() => {
            nextRound();
        }, 700);

        return () => window.clearTimeout(timer);
    }, [choice, done]);

    const correctChoice = choice === trial.correctSide;
    const options = [
        { side: 'left', dots: trial.left.dots },
        { side: 'right', dots: trial.right.dots },
    ];

    return (
        <GameShell
            {...props}
            copy={copy}
            metrics={{ level, maxScore: maxRounds, score, streak }}
            tone={correctChoice || done ? 'good' : choice ? 'miss' : 'ready'}
        >
            <div className="UFOV-brain-games__estimate-game">
                <div className="UFOV-brain-games__game-cue">{copy.estimateDuelCue}</div>
                <div className="UFOV-brain-games__estimate-board">
                    {options.map((option, index) => {
                        const picked = choice === option.side;
                        const correct = choice && trial.correctSide === option.side;

                        return (
                            <button
                                aria-label={formatGameCopy(copy.answerOption, { number: index + 1 })}
                                className={`UFOV-brain-games__estimate-card ${picked ? 'is-picked' : ''} ${correct ? 'is-correct' : ''}`.trim()}
                                disabled={done || Boolean(choice)}
                                key={`${trial.id}-${option.side}`}
                                onClick={() => choose(option.side)}
                                type="button"
                            >
                                {option.dots.map((dot) => (
                                    <span
                                        aria-hidden="true"
                                        className="UFOV-brain-games__estimate-dot"
                                        key={`${option.side}-${dot.id}`}
                                        style={{
                                            height: `${dot.size}px`,
                                            left: `${dot.x}%`,
                                            opacity: dot.opacity,
                                            top: `${dot.y}%`,
                                            width: `${dot.size}px`,
                                        }}
                                    />
                                ))}
                            </button>
                        );
                    })}
                </div>
                <div
                    aria-label={formatGameCopy(copy.streak, { count: streak })}
                    className="UFOV-brain-games__streak-number"
                >
                    {streak}
                </div>
            </div>
            {done ? (
                <GameResultOverlay metrics={{ level, maxScore: maxRounds, score, streak }} onRestart={reset} />
            ) : null}
        </GameShell>
    );
}

export default EstimateDuelGame;
